import React, { useEffect, useState } from "react";
import API from "../services/api";

function TherapistResourcesPage() {
  // -------------------------------------------------------
  //                      REFERRALS
  // -------------------------------------------------------
  const [referrals, setReferrals] = useState([]);

  // For creating new referral
  const [referralName, setReferralName] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState("");
  const [population, setPopulation] = useState("");
  const [referralCategory, setReferralCategory] = useState("");
  const [referralNotes, setReferralNotes] = useState("");

  // Sorting state for referrals
  const [sortField, setSortField] = useState("dateAdded");
  const [sortAsc, setSortAsc] = useState(false);

  // Modal for Referrals
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [isEditingReferral, setIsEditingReferral] = useState(false);

  // Modal fields for editing referral
  const [modalRefName, setModalRefName] = useState("");
  const [modalRefPhone, setModalRefPhone] = useState("");
  const [modalRefWebsite, setModalRefWebsite] = useState("");
  const [modalRefAddress, setModalRefAddress] = useState("");
  const [modalRefPopulation, setModalRefPopulation] = useState("");
  const [modalRefCategory, setModalRefCategory] = useState("");
  const [modalRefNotes, setModalRefNotes] = useState("");

  // Updated categories with "Psychotherapist"
  const referralCategories = [
    "Psychiatrist",
    "Psychologist",
    "Psychotherapist", // Added
    "Social Worker",
    "Substance Abuse Center",
    "Child/Adolescent Specialist",
    "Couples/Family Therapy",
    "Legal/Forensic",
    "Community Resource",
  ];

  const populationOptions = [
    "Children",
    "Adolescents/Teens",
    "Adults",
    "Older Adults/Geriatric",
    "Couples",
    "Families",
    "All Ages",
  ];

  // -------------------------------------------------------
  //                      RESOURCES
  // -------------------------------------------------------
  const [resources, setResources] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("All");

  // For creating new resource
  const [title, setTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");

  // Modal for Resources
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [isEditingResource, setIsEditingResource] = useState(false);

  // Modal fields for editing resource
  const [modalResTitle, setModalResTitle] = useState("");
  const [modalResCategory, setModalResCategory] = useState("");
  const [modalResDescription, setModalResDescription] = useState("");
  const [modalResLink, setModalResLink] = useState("");
  const [modalResUploader, setModalResUploader] = useState("");
  const [modalResCreatedAt, setModalResCreatedAt] = useState("");
  const [modalResFileUrl, setModalResFileUrl] = useState("");
  const [modalResNewFile, setModalResNewFile] = useState(null);

  const resourceCategories = [
    "Clinical Forms & Templates",
    "Practice Policies & Compliance",
    "Client Handouts & Psychoeducation",
    "Professional Development & Training",
    "Marketing & Outreach",
    "Reference Materials & Research",
    "Miscellaneous / General Resources",
  ];

  // -------------------------------------------------------
  //                 FETCH DATA
  // -------------------------------------------------------
  useEffect(() => {
    fetchReferrals();
    fetchResources();
  }, []);

  // ------ REFERRALS ------
  const fetchReferrals = async () => {
    try {
      const res = await API.get("/resources/referrals");
      setReferrals(res.data);
    } catch (err) {
      console.error(err);
      alert("Error fetching referrals");
    }
  };

  const handleAddReferral = async (e) => {
    e.preventDefault();
    if (!referralName.trim()) {
      alert("Please provide a referral name.");
      return;
    }
    try {
      const body = {
        name: referralName,
        phone,
        website,
        address,
        population,
        category: referralCategory,
        notes: referralNotes,
      };
      await API.post("/resources/referrals", body);
      alert("Referral added!");
      setReferralName("");
      setPhone("");
      setWebsite("");
      setAddress("");
      setPopulation("");
      setReferralCategory("");
      setReferralNotes("");
      fetchReferrals();
    } catch (err) {
      console.error(err);
      alert("Error adding referral");
    }
  };

  const handleDeleteReferral = async (id) => {
    if (!window.confirm("Delete this referral?")) return;
    try {
      await API.delete(`/resources/referrals/${id}`);
      alert("Referral deleted");
      fetchReferrals();
      if (selectedReferral && selectedReferral.id === id) {
        setShowReferralModal(false);
        setSelectedReferral(null);
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting referral");
    }
  };

  const handleShowReferralModal = (referral) => {
    setSelectedReferral(referral);
    setModalRefName(referral.name || "");
    setModalRefPhone(referral.phone || "");
    setModalRefWebsite(referral.website || "");
    setModalRefAddress(referral.address || "");
    setModalRefPopulation(referral.population || "");
    setModalRefCategory(referral.category || "");
    setModalRefNotes(referral.notes || "");
    setIsEditingReferral(false);
    setShowReferralModal(true);
  };

  const handleSaveReferralChanges = async () => {
    if (!modalRefName.trim()) {
      alert("Referral name is required.");
      return;
    }
    try {
      const body = {
        name: modalRefName,
        phone: modalRefPhone,
        website: modalRefWebsite,
        address: modalRefAddress,
        population: modalRefPopulation,
        category: modalRefCategory,
        notes: modalRefNotes,
      };
      await API.put(`/resources/referrals/${selectedReferral.id}`, body);
      alert("Referral updated!");
      fetchReferrals();
      setShowReferralModal(false);
      setSelectedReferral(null);
    } catch (err) {
      console.error(err);
      alert("Error updating referral");
    }
  };

  // Sort referrals
  const sortedReferrals = [...referrals].sort((a, b) => {
    if (sortField === "dateAdded") {
      const dateA = new Date(a.dateAdded);
      const dateB = new Date(b.dateAdded);
      return sortAsc ? dateA - dateB : dateB - dateA;
    } else {
      const valA = (a[sortField] || "").toLowerCase();
      const valB = (b[sortField] || "").toLowerCase();
      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // ------ RESOURCES ------
  const fetchResources = async () => {
    try {
      const res = await API.get("/resources");
      setResources(res.data);
    } catch (err) {
      console.error(err);
      alert("Error fetching resources");
    }
  };

  const displayedResources = resources.filter((r) =>
    categoryFilter === "All" ? true : r.category === categoryFilter
  );

  const handleUploadResource = async (e) => {
    e.preventDefault();
    if (!title.trim() || !selectedCategory) {
      alert("Please provide a title and category.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("category", selectedCategory);
      formData.append("description", description);
      formData.append("link", link);
      if (file) {
        formData.append("file", file);
      }
      await API.post("/resources/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Resource uploaded successfully!");
      setTitle("");
      setSelectedCategory("");
      setFile(null);
      setDescription("");
      setLink("");
      fetchResources();
    } catch (err) {
      console.error(err);
      alert("Error uploading resource");
    }
  };

  const handleDeleteResource = async (id) => {
    if (!window.confirm("Are you sure you want to delete this resource?")) return;
    try {
      await API.delete(`/resources/${id}`);
      alert("Resource deleted successfully!");
      fetchResources();
      if (selectedResource && selectedResource.id === id) {
        setShowResourceModal(false);
        setSelectedResource(null);
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting resource");
    }
  };

  // Show "More Info" modal for resources
  const handleShowResourceModal = (resItem) => {
    setSelectedResource(resItem);
    setModalResTitle(resItem.title || "");
    setModalResCategory(resItem.category || "");
    setModalResDescription(resItem.description || "");
    setModalResLink(resItem.link || "");
    setModalResFileUrl(resItem.file_url || "");
    setModalResUploader(resItem.uploader_name || "");
    setModalResCreatedAt(resItem.created_at || "");
    setModalResNewFile(null);
    setIsEditingResource(false);
    setShowResourceModal(true);
  };

  const handleSaveResourceChanges = async () => {
    if (!modalResTitle.trim() || !modalResCategory.trim()) {
      alert("Title and category are required.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("title", modalResTitle);
      formData.append("category", modalResCategory);
      formData.append("description", modalResDescription);
      formData.append("link", modalResLink);
      if (modalResNewFile) {
        formData.append("file", modalResNewFile);
      }
      await API.put(`/resources/${selectedResource.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Resource updated successfully!");
      fetchResources();
      setShowResourceModal(false);
      setSelectedResource(null);
    } catch (err) {
      console.error(err);
      alert("Error updating resource");
    }
  };

  const API_BASE = "https://www.avenuesportal.com";

  return (
    <div style={{ margin: "1rem", background: "#f8f8f8", padding: "1rem" }}>
      <h2>Therapist Portal</h2>

      {/* --------------------------------------------------- */}
      {/*                      REFERRALS                      */}
      {/* --------------------------------------------------- */}
      <div style={{ background: "#fff", padding: "1rem", marginBottom: "2rem" }}>
        <h3>Referral List</h3>

        {/* Add New Referral */}
        <form onSubmit={handleAddReferral} style={{ marginBottom: "1rem" }}>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Name / Practice:</label>
            <input
              type="text"
              value={referralName}
              onChange={(e) => setReferralName(e.target.value)}
              style={{ width: "220px" }}
            />
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Phone:</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{ width: "220px" }}
              placeholder="(555) 555-5555"
            />
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Website:</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              style={{ width: "220px" }}
              placeholder="https://..."
            />
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Address:</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={{ width: "220px" }}
            />
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Population:</label>
            <select
              value={population}
              onChange={(e) => setPopulation(e.target.value)}
              style={{ width: "230px" }}
            >
              <option value="">-- Select --</option>
              {populationOptions.map((pop) => (
                <option key={pop} value={pop}>
                  {pop}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Category:</label>
            <select
              value={referralCategory}
              onChange={(e) => setReferralCategory(e.target.value)}
              style={{ width: "230px" }}
            >
              <option value="">-- Select --</option>
              {referralCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Notes:</label>
            <textarea
              value={referralNotes}
              onChange={(e) => setReferralNotes(e.target.value)}
              style={{ width: "220px", height: "60px" }}
            />
          </div>
          <button type="submit">Add Referral</button>
        </form>

        {/* Table: Name, Phone, Website, Category, More Info */}
        <div style={{ overflowX: "auto", fontSize: "0.9rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "auto",
              background: "#fff",
            }}
          >
            <thead>
              <tr style={{ background: "#f2f2f2" }}>
                <th
                  style={{
                    padding: "8px",
                    cursor: "pointer",
                    borderBottom: "1px solid #ccc",
                    textAlign: "left",
                  }}
                  onClick={() => handleSort("name")}
                >
                  Name
                </th>
                <th
                  style={{
                    padding: "8px",
                    cursor: "pointer",
                    borderBottom: "1px solid #ccc",
                    textAlign: "left",
                  }}
                  onClick={() => handleSort("phone")}
                >
                  Phone
                </th>
                <th
                  style={{
                    padding: "8px",
                    cursor: "pointer",
                    borderBottom: "1px solid #ccc",
                    textAlign: "left",
                  }}
                  onClick={() => handleSort("website")}
                >
                  Website
                </th>
                {/* New Category column */}
                <th
                  style={{
                    padding: "8px",
                    cursor: "pointer",
                    borderBottom: "1px solid #ccc",
                    textAlign: "left",
                  }}
                  onClick={() => handleSort("category")}
                >
                  Category
                </th>
                <th
                  style={{
                    padding: "8px",
                    borderBottom: "1px solid #ccc",
                    textAlign: "left",
                  }}
                >
                  More Info
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedReferrals.map((r, idx) => {
                const websiteIsLink =
                  r.website && (r.website.includes("http") || r.website.includes("www"));
                return (
                  <tr key={r.id} style={{ background: idx % 2 === 0 ? "#fafafa" : "white" }}>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                      {r.name || "-"}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                      {r.phone || "-"}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                      {websiteIsLink ? (
                        <a href={r.website} target="_blank" rel="noreferrer">
                          {r.website}
                        </a>
                      ) : (
                        r.website || "-"
                      )}
                    </td>
                    {/* Show category in the table */}
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                      {r.category || "-"}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                      <button onClick={() => handleShowReferralModal(r)}>More Info</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Referral Modal */}
      {showReferralModal && selectedReferral && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "5px",
              width: "400px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            {!isEditingReferral ? (
              <>
                <h2>{modalRefName || "-"}</h2>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Phone:</strong> {modalRefPhone || "-"}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Website:</strong>{" "}
                  {modalRefWebsite ? (
                    <a href={modalRefWebsite} target="_blank" rel="noreferrer">
                      {modalRefWebsite}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Address:</strong> {modalRefAddress || "-"}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Population:</strong> {modalRefPopulation || "-"}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Category:</strong> {modalRefCategory || "-"}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Notes:</strong> {modalRefNotes || "-"}
                </div>
                <div style={{ marginBottom: "1rem" }}>
                  <strong>Date Added:</strong>{" "}
                  {selectedReferral.dateAdded
                    ? new Date(selectedReferral.dateAdded).toLocaleDateString()
                    : "-"}
                </div>
                <button onClick={() => setIsEditingReferral(true)}>Edit</button>
                <button
                  onClick={() => handleDeleteReferral(selectedReferral.id)}
                  style={{ marginLeft: "0.5rem" }}
                >
                  Delete
                </button>
                <button
                  onClick={() => {
                    setShowReferralModal(false);
                    setSelectedReferral(null);
                  }}
                  style={{ marginLeft: "0.5rem" }}
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <h3>Edit Referral</h3>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "100px" }}>Name:</label>
                  <input
                    type="text"
                    value={modalRefName}
                    onChange={(e) => setModalRefName(e.target.value)}
                    style={{ width: "220px" }}
                  />
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "100px" }}>Phone:</label>
                  <input
                    type="text"
                    value={modalRefPhone}
                    onChange={(e) => setModalRefPhone(e.target.value)}
                    style={{ width: "220px" }}
                  />
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "100px" }}>Website:</label>
                  <input
                    type="text"
                    value={modalRefWebsite}
                    onChange={(e) => setModalRefWebsite(e.target.value)}
                    style={{ width: "220px" }}
                  />
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "100px" }}>Address:</label>
                  <input
                    type="text"
                    value={modalRefAddress}
                    onChange={(e) => setModalRefAddress(e.target.value)}
                    style={{ width: "220px" }}
                  />
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "100px" }}>Population:</label>
                  <select
                    value={modalRefPopulation}
                    onChange={(e) => setModalRefPopulation(e.target.value)}
                    style={{ width: "230px" }}
                  >
                    <option value="">-- Select --</option>
                    {populationOptions.map((pop) => (
                      <option key={pop} value={pop}>
                        {pop}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "100px" }}>Category:</label>
                  <select
                    value={modalRefCategory}
                    onChange={(e) => setModalRefCategory(e.target.value)}
                    style={{ width: "230px" }}
                  >
                    <option value="">-- Select --</option>
                    {referralCategories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "100px" }}>Notes:</label>
                  <textarea
                    value={modalRefNotes}
                    onChange={(e) => setModalRefNotes(e.target.value)}
                    style={{ width: "220px", height: "60px" }}
                  />
                </div>
                <button onClick={handleSaveReferralChanges}>Save</button>
                <button
                  onClick={() => setIsEditingReferral(false)}
                  style={{ marginLeft: "0.5rem" }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* --------------------------------------------------- */}
      {/*                     RESOURCES                       */}
      {/* --------------------------------------------------- */}
      <div style={{ background: "#fff", padding: "1rem", marginBottom: "2rem" }}>
        <h3>Resources</h3>
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ marginRight: "0.5rem" }}>Filter by Category:</label>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="All">All</option>
            {resourceCategories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Resources Table: matching font size */}
        {displayedResources.length === 0 ? (
          <p>No resources found in this category.</p>
        ) : (
          <div style={{ overflowX: "auto", fontSize: "0.9rem" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "auto",
                background: "#fff",
              }}
            >
              <thead>
                <tr style={{ background: "#f2f2f2" }}>
                  <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                    Title
                  </th>
                  <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                    Category
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ccc",
                      textAlign: "left",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Link
                  </th>
                  <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "left" }}>
                    File
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ccc",
                      textAlign: "left",
                    }}
                  >
                    More Info
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedResources.map((resItem, idx) => {
                  return (
                    <tr key={resItem.id} style={{ background: idx % 2 === 0 ? "#fafafa" : "white" }}>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #ccc",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {resItem.title}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #ccc",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {resItem.category}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #ccc",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {resItem.link ? (
                          <a href={resItem.link} target="_blank" rel="noreferrer">
                            External
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #ccc",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {resItem.file_url ? (
                          <a href={`${API_BASE}${resItem.file_url}`} target="_blank" rel="noreferrer">
                            Open
                          </a>
                        ) : (
                          "No file"
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #ccc",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <button onClick={() => handleShowResourceModal(resItem)}>More Info</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resource Modal */}
      {showResourceModal && selectedResource && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "5px",
              width: "500px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            {!isEditingResource ? (
              <>
                <h2>{modalResTitle}</h2>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Category:</strong> {modalResCategory || "-"}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Description:</strong> {modalResDescription || "-"}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Link:</strong>{" "}
                  {modalResLink ? (
                    <a href={modalResLink} target="_blank" rel="noreferrer">
                      {modalResLink}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Uploader:</strong> {modalResUploader || "-"}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Uploaded:</strong>{" "}
                  {modalResCreatedAt
                    ? new Date(modalResCreatedAt).toLocaleDateString()
                    : "-"}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>File:</strong>{" "}
                  {modalResFileUrl ? (
                    <a href={`${API_BASE}${modalResFileUrl}`} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  ) : (
                    "No file"
                  )}
                </div>
                <button onClick={() => setIsEditingResource(true)}>Edit</button>
                <button
                  onClick={() => handleDeleteResource(selectedResource.id)}
                  style={{ marginLeft: "0.5rem" }}
                >
                  Delete
                </button>
                <button
                  onClick={() => {
                    setShowResourceModal(false);
                    setSelectedResource(null);
                  }}
                  style={{ marginLeft: "0.5rem" }}
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <h2>Edit Resource</h2>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "120px" }}>Title:</label>
                  <input
                    type="text"
                    value={modalResTitle}
                    onChange={(e) => setModalResTitle(e.target.value)}
                    style={{ width: "300px" }}
                  />
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "120px" }}>Category:</label>
                  <select
                    value={modalResCategory}
                    onChange={(e) => setModalResCategory(e.target.value)}
                    style={{ width: "310px" }}
                  >
                    <option value="">-- Choose Category --</option>
                    {resourceCategories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "120px" }}>Description:</label>
                  <textarea
                    value={modalResDescription}
                    onChange={(e) => setModalResDescription(e.target.value)}
                    style={{ width: "300px", height: "80px" }}
                  />
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "120px" }}>Link:</label>
                  <input
                    type="url"
                    value={modalResLink}
                    onChange={(e) => setModalResLink(e.target.value)}
                    style={{ width: "300px" }}
                  />
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <label style={{ display: "inline-block", width: "120px" }}>Replace File:</label>
                  <input
                    type="file"
                    onChange={(e) => setModalResNewFile(e.target.files[0])}
                    accept="image/*,application/pdf,
                            application/msword,
                            application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  />
                  <p style={{ fontSize: "0.9em" }}>
                    (Leave blank if you don't want to replace the existing file)
                  </p>
                </div>
                <button onClick={handleSaveResourceChanges}>Save</button>
                <button
                  onClick={() => setIsEditingResource(false)}
                  style={{ marginLeft: "0.5rem" }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* --------------------------------------------------- */}
      {/*             UPLOAD NEW RESOURCE FORM               */}
      {/* --------------------------------------------------- */}
      <div style={{ background: "#fff", padding: "1rem" }}>
        <h3>Upload New Resource</h3>
        <form onSubmit={handleUploadResource} encType="multipart/form-data">
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Title:</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{ width: "250px" }}
            />
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ width: "260px" }}
            >
              <option value="">-- Choose Category --</option>
              {resourceCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Description:</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ width: "250px", height: "80px" }}
            />
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>Resource Link:</label>
            <input
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              style={{ width: "250px" }}
            />
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={{ display: "inline-block", width: "120px" }}>File (optional):</label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              accept="image/*,application/pdf,
                      application/msword,
                      application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            />
          </div>
          <button type="submit">Upload</button>
        </form>
      </div>
    </div>
  );
}

export default TherapistResourcesPage;
